import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSwitcher from './LanguageSwitcher';

// Logo component avec le style TERRE et MER
const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    {/* Wave icon */}
    <div className="flex flex-col items-center justify-center w-10">
      <div className="w-8 h-1 bg-[#2D3E50] rounded-full mb-1"></div>
      <svg width="32" height="16" viewBox="0 0 32 16" className="wave-icon">
        <path d="M0 8 Q8 0, 16 8 T32 8" stroke="#E8945A" strokeWidth="2.5" fill="none"/>
      </svg>
      <svg width="32" height="12" viewBox="0 0 32 12" className="wave-icon" style={{animationDelay: '0.3s'}}>
        <path d="M0 6 Q8 0, 16 6 T32 6" stroke="#2D3E50" strokeWidth="2" fill="none"/>
      </svg>
    </div>
    {/* Brand name */}
    <div className="hidden sm:block">
      <div className="flex items-baseline">
        <span className="font-['Playfair_Display'] font-semibold text-xl text-[#2D3E50] tracking-tight">TERRE</span>
        <span className="font-['Playfair_Display'] font-normal text-xl text-[#E8945A] italic mx-0.5">et</span>
        <span className="font-['Playfair_Display'] font-semibold text-xl text-[#2D3E50] tracking-tight">MER</span>
      </div>
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#64748B]">Location de Linge</span>
    </div>
  </Link>
);

const Header = () => {
  const { user, logout } = useAuth();
  const { totalItems, openCart } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-7">
            <Link
              to="/"
              className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] transition-colors"
              data-testid="nav-home"
            >
              Accueil
            </Link>
            <Link 
              to="/products" 
              className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] transition-colors"
              data-testid="nav-products"
            >
              Nos Produits
            </Link>
            <Link
              to="/implantations"
              className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] transition-colors"
              data-testid="nav-implantations"
            >
              Nos implantations
            </Link>
            <Link
              to="/check-in-out"
              className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] transition-colors"
              data-testid="nav-checkinout"
            >
              Check-in / Check-out
            </Link>
            <a 
              href="/#comment-ca-marche" 
              className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] transition-colors"
            >
              Comment ça marche
            </a>
            <Link
              to="/faq"
              className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] transition-colors"
              data-testid="nav-faq"
            >
              FAQ
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Cart Button */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={openCart}
              data-testid="cart-trigger"
            >
              <ShoppingBag className="h-5 w-5 text-[#64748B]" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E8945A] text-white text-xs rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Button>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                    <User className="h-5 w-5 text-[#64748B]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-link">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Mon espace
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                      <ShieldCheck className="mr-2 h-4 w-4 text-[#E8945A]" />
                      Espace admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="bg-[#2D3E50] hover:bg-[#3D4F61] text-white rounded-lg"
                onClick={() => navigate('/login')}
                data-testid="login-btn"
              >
                Connexion
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 animate-fade-in">
            <nav className="flex flex-col gap-3">
              <Link
                to="/"
                className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Accueil
              </Link>
              <Link 
                to="/products" 
                className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Nos Produits
              </Link>
              <Link
                to="/implantations"
                className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Nos implantations
              </Link>
              <Link
                to="/check-in-out"
                className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Check-in / Check-out
              </Link>
              <a 
                href="/#comment-ca-marche" 
                className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Comment ça marche
              </a>
              <Link
                to="/faq"
                className="text-sm font-medium text-[#64748B] hover:text-[#2D3E50] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
